// Base libs
import { shallowEqual } from "react-redux";
import {
    GuxDropdown,
    GuxListbox,
    GuxOption,
} from "genesys-spark-components-react";
import {
    EvaluationQuestionTypeEnum,
    SurveyQuestionTypeEnum,
} from "@genesys/cloud-frontend/models";

// General common
import { useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
import { useStrings } from "features/i18n/useStrings";
import FeatureToggle from "enums/FeatureToggle";

// Feature common
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";

// Feature common slices
import {
    selectAnswerOption,
    selectAnswerOptionsForQuestion,
    selectFormType,
    selectQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";

// Locales
import strings from "./VisibilityCondition.strings";

// Locals
import NPSScoreCategories from "../NPSScoreCategories";

function DropDown({
    selectedId,
    setSelectedId,
    dropDownOptions,
    placeholder,
}: {
    dropDownOptions?: JSX.Element[];
    selectedId: string;
    setSelectedId: (q: string) => void;
    placeholder: string;
}) {
    const t = useStrings(strings);

    return (
        <GuxDropdown
            placeholder={placeholder}
            onInput={(event: React.FormEvent<HTMLGuxDropdownElement>) => {
                setSelectedId(event.currentTarget.value);
            }}
            value={selectedId}
        >
            <GuxListbox aria-label={t.labels.dropDownOptions}>
                {dropDownOptions}
            </GuxListbox>
        </GuxDropdown>
    );
}

function QuestionDropDownOption({ questionId }: { questionId: string }) {
    const t = useStrings(strings);
    const questionName = useAppSelector(
        (state) => selectQuestion(state, questionId)?.text ?? ""
    );
    return (
        <GuxOption value={questionId} key={questionId}>
            {questionName.trim() === "" ? t.labels.noText : questionName}
        </GuxOption>
    );
}
export function QuestionDropDown({
    questionIds,
    selectedQuestionId,
    onUpdate,
}: {
    questionIds: Array<string>;
    selectedQuestionId: string;
    onUpdate: (conditionalQuestionId: string) => void;
}) {
    const t = useStrings(strings);

    const dropDownQuestionOptions = questionIds.map((questionId) => {
        return (
            <QuestionDropDownOption questionId={questionId} key={questionId} />
        );
    });

    return (
        <DropDown
            dropDownOptions={dropDownQuestionOptions}
            selectedId={selectedQuestionId}
            setSelectedId={(id) => {
                onUpdate(id);
            }}
            placeholder={t.labels.selectQuestion}
        />
    );
}

function AnswerDropDownOption({
    questionId,
    answerId,
}: {
    questionId: string;
    answerId: string;
}) {
    const t = useStrings(strings);
    const isMultipleSelectFTEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT)
    );

    const questionType = useAppSelector(
        (state) => selectQuestion(state, questionId)?.type
    );

    const answerName = useAppSelector((state) => {
        if (
            selectFormType(state) === FormType.SURVEY &&
            Object.keys(NPSScoreCategories).includes(answerId)
        ) {
            return questionType === SurveyQuestionTypeEnum.NpsQuestion
                ? t.npsAnswerOptions[answerId as NPSScoreCategories]
                : answerId;
        }
        const isMultipleSelect =
            isMultipleSelectFTEnabled &&
            questionType === EvaluationQuestionTypeEnum.MultipleSelectQuestion;
        return (
            selectAnswerOption(state, answerId, isMultipleSelect)?.text ?? ""
        );
    });
    return (
        <GuxOption value={answerId} key={answerId}>
            {answerName.trim() === "" ? t.labels.noText : answerName}
        </GuxOption>
    );
}
export function AnswerDropDown({
    questionId,
    selectedAnswerId,
    onUpdate,
    existingVisibilityConditionAnswers,
}: {
    questionId: string;
    selectedAnswerId: string;
    existingVisibilityConditionAnswers: Set<string>;
    onUpdate: (conditionalQuestionId: string) => void;
}) {
    const answerOptionsForSelectedQuestion = useAppSelector((state) => {
        return selectAnswerOptionsForQuestion(state, questionId)?.filter(
            (answerId) =>
                !existingVisibilityConditionAnswers.has(answerId) ||
                answerId === selectedAnswerId
        );
    }, shallowEqual);

    const dropDownAnswerOptions = answerOptionsForSelectedQuestion?.map(
        (answerId: string) => {
            return (
                <AnswerDropDownOption
                    questionId={questionId}
                    answerId={answerId}
                    key={answerId}
                />
            );
        }
    );
    const t = useStrings(strings);

    return (
        <>
            <DropDown
                dropDownOptions={dropDownAnswerOptions}
                selectedId={selectedAnswerId}
                setSelectedId={(answerId) => onUpdate(answerId)}
                placeholder={t.labels.selectAnswer}
            />
        </>
    );
}
